import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { AsyncSelectComponent } from '~/components/Shared/Filters_DEPRECATED/AsyncSelectComponent';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Field } from '~/evifields/src/types/Field';
import { Filter } from '~/evifields/src/types/Filter';
import {
  ClauseValue,
  TextSearch,
  TextSearchContains,
  TextSearchScope,
} from '~/evifields/src/types/Value';
import { LabeledOperator } from '~/features/advanced-search/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Nullable } from '~/types/Nullable';

interface Props {
  field: Field<'clause_v2'>;
  filter: Filter<ClauseValue>;
  onChange: (updatedValues: ClauseValue[]) => void;
}

const ClauseValues = ({ field, filter, onChange }: Props) => {
  const { render, settings } = field;
  const { textSearchFilter } = settings;
  const { id, values, operatorId } = filter;
  const [clauseValue] = values;
  const textSearch = clauseValue?.text_search[0] || null;
  const provisionValue = clauseValue?.provision || null;
  const textSearchOperatorValue = textSearch?.scope?.value || null;
  const textSearchValue = textSearch?.text || null;
  const hasProvision = Boolean(provisionValue);
  const isContainsAny = operatorId === 'contains_any';
  const shouldEnableSearchWithClause = hasProvision && isContainsAny;

  const [draftClauseOperatorId, setDraftClauseOperatorId] = useState<
    Nullable<string>
  >(textSearchOperatorValue);
  const [draftClauseText, setDraftClauseText] =
    useState<Nullable<string>>(textSearchValue);
  const user = useCurrentUser();
  const intl = useIntl();
  const [
    shouldSearchWithinClause,
    _toggleSearchWithinClause,
    enableSearchWithinClause,
    disableSearchWithinClause,
  ] = useToggle(false);

  useEffect(() => {
    if (!isContainsAny) {
      handleCancelClauseSearch();
    }
  }, [isContainsAny]);

  const handleClauseProvisionChange = (
    updatedClauseProvision: Nullable<string>,
  ) => {
    if (updatedClauseProvision !== null) {
      const updatedClause = {
        provision: updatedClauseProvision,
        text_search: [],
      };
      onChange([updatedClause]);
    }
  };

  const handleCancelClauseSearch = () => {
    resetDraft(textSearch);
    disableSearchWithinClause();
    trackSegment('Clause Type - Cancel', {
      user_id: user.id,
      client_id: user.client,
    });
  };

  const handleConfirmClauseSearch = () => {
    trackSegment('Clause Type - Confirm', {
      user_id: user.id,
      client_id: user.client,
    });

    let updatedTextSearch: TextSearch[] = [];
    if (draftClauseOperatorId) {
      const contains = isContainsAny
        ? {
            value: 'contains',
            label: intl.formatMessage({
              id: 'ClauseValues.containsLabel',
              defaultMessage: 'contains',
              description: 'Label for contains operator',
            }),
          }
        : {
            value: 'does_not_contain',
            label: intl.formatMessage({
              id: 'ClauseValues.doesNotContainLabel',
              defaultMessage: 'does not contain',
              description: 'Label for does not contain operator',
            }),
          };
      const textOperator = textSearchFilter?.operators.find(
        (op: LabeledOperator) => op.id === draftClauseOperatorId,
      );
      const scope = {
        value: textOperator?.id || '',
        label: textOperator?.label || '',
      };

      updatedTextSearch = [
        {
          contains: contains as TextSearchContains,
          scope: scope as TextSearchScope,
          text: draftClauseText || '',
        },
      ];
    }
    const updatedClauseValues: ClauseValue[] = provisionValue
      ? [
          {
            ...clauseValue,
            provision: provisionValue,
            text_search: updatedTextSearch,
          },
        ]
      : [];

    onChange(updatedClauseValues);
    disableSearchWithinClause();
  };

  const resetDraft = (textSearch: Nullable<TextSearch>) => {
    let updatedDraftClauseOperatorId = null;
    let updatedDraftClauseText = null;
    if (textSearch) {
      updatedDraftClauseOperatorId = textSearch.scope.value;
      updatedDraftClauseText = textSearch.text;
    }

    setDraftClauseOperatorId(updatedDraftClauseOperatorId);
    setDraftClauseText(updatedDraftClauseText);
  };

  const innerProps = {
    enableOptionIndicator: true,
    enableSearchIcon: true,
    enableControl: true,
    isEmbedded: true,
    isClearable: false,
    isMulti: false,
    name: id,
    placeholder: intl.formatMessage({
      id: 'ClauseValues.searchPlaceholder',
      defaultMessage: 'Search for a clause...',
      description: 'Placeholder text for clause search input',
    }),
    value: provisionValue,
    onChange: handleClauseProvisionChange,
  };

  const clauseContent = (
    <>
      <Layout h={200} overflowY="auto">
        {render?.({
          Component: !shouldSearchWithinClause
            ? AsyncSelectComponent
            : () => <></>,
          innerProps,
        })}
      </Layout>

      {shouldSearchWithinClause && (
        <Layout direction="column" px={px} spacing={2}>
          <Label>{provisionValue}</Label>
          <ClauseOperatorSelect
            value={draftClauseOperatorId}
            onChange={setDraftClauseOperatorId}
            clauseOptions={textSearchFilter?.operators || []}
          />
          <TextArea
            name="clause-text"
            value={draftClauseText}
            onChange={setDraftClauseText}
          />
        </Layout>
      )}
    </>
  );

  const actions = shouldSearchWithinClause ? (
    <Layout px={2} justify="space-between" preset="buttons">
      <Button preset="cancel" onClick={handleCancelClauseSearch} />
      <Button
        disabled={!(draftClauseOperatorId && draftClauseText)}
        text={intl.formatMessage({
          id: 'ClauseValues.confirmButton',
          defaultMessage: 'Confirm',
          description: 'Confirm button text for clause search',
        })}
        variant="action"
        onClick={handleConfirmClauseSearch}
      />
    </Layout>
  ) : (
    shouldEnableSearchWithClause && (
      <Layout direction="column" spacing={4}>
        <Divider />
        <Button
          text={intl.formatMessage({
            id: 'ClauseValues.searchWithinClauseButton',
            defaultMessage: 'Search within clause text',
            description: 'Button text to enable searching within clause text',
          })}
          variant="action"
          onClick={enableSearchWithinClause}
        />
      </Layout>
    )
  );

  return (
    <Box mx={-px}>
      {clauseContent}
      {actions}
    </Box>
  );
};

interface ClauseOperatorSelectProps {
  value: Nullable<string>;
  onChange: (updatedClauseOperatorId: Nullable<string>) => void;
  clauseOptions: LabeledOperator[];
}

const ClauseOperatorSelect = ({
  value,
  onChange,
  clauseOptions,
}: ClauseOperatorSelectProps) => {
  const intl = useIntl();

  return (
    <Select
      isClearable={false}
      isMulti={false}
      isSearchable={false}
      enablePortal
      name="clause-operator"
      options={clauseOptions.map((textOperator) => ({
        value: textOperator.id,
        label: textOperator.label,
      }))}
      placeholder={intl.formatMessage({
        id: 'Filters.ClauseValues.chooseOperatorPlaceholder',
        defaultMessage: 'Choose an operator',
        description: 'Placeholder text for operator selection',
      })}
      value={value}
      onChange={onChange}
    />
  );
};

const px = 4;

export default ClauseValues;
